import React from 'react';

// Design-system card surface (handoff bundle's Card spec on app tokens).
// Cards rest on shadow-sm at 13px radius; interactive cards lift on hover.

type Padding = 'none' | 'sm' | 'md' | 'lg';
type Elevation = 'flat' | 'default' | 'raised';

const PADDING: Record<Padding, string> = {
  none: '',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
};

const ELEVATION: Record<Elevation, string> = {
  flat: '',
  default: 'shadow-sm',
  raised: 'shadow-md',
};

interface CardOwnProps {
  padding?: Padding;
  elevation?: Elevation;
  /** Brand-green border for highlighted/selected cards. */
  accent?: boolean;
  /** Clickable card: hover-lift + press states; renders a <button>. */
  interactive?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export type CardProps = CardOwnProps &
  (
    | ({ interactive: true } & React.ButtonHTMLAttributes<HTMLButtonElement>)
    | ({ interactive?: false } & React.HTMLAttributes<HTMLElement>)
  );

export function Card({
  padding = 'md',
  elevation = 'default',
  accent = false,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps) {
  const classes = [
    'rounded-lg border bg-card',
    accent ? 'border-green-300' : 'border-slate-200',
    PADDING[padding],
    ELEVATION[elevation],
    interactive
      ? 'block w-full cursor-pointer text-left transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:shadow-sm'
      : '',
    className ?? '',
  ].join(' ');

  if (interactive) {
    return (
      <button type="button" className={classes} {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
        {children}
      </button>
    );
  }
  return (
    <section className={classes} {...(rest as React.HTMLAttributes<HTMLElement>)}>
      {children}
    </section>
  );
}
